import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { PenLine, Loader2, CheckCircle2 } from 'lucide-react';
import { SignaturePad } from './SignaturePad';
import { API_URL } from './bookingUtils';

const defaultNote = (company, customer, ref) =>
  `Dear ${customer || 'Customer'},\n\nWe are pleased to confirm your booking (Ref ${ref}). This document, signed by both parties, serves as your official booking confirmation. Please retain it for your records.\n\nThank you for choosing ${company}.`;

export const CompanySignDialog = ({ open, onOpenChange, booking, onSigned }) => {
  const [branding, setBranding] = useState(null);
  const [form, setForm] = useState({ signatory_name: '', signatory_title: '', confirmation_note: '' });
  const [signature, setSignature] = useState(null);
  const [useSaved, setUseSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/api/booking-forms/branding`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : {}))
      .then(b => {
        setBranding(b);
        setForm({
          signatory_name: b.signatory_name || '',
          signatory_title: b.signatory_title || '',
          confirmation_note: defaultNote(b.company_name || 'Goldsands Group Inc', booking?.customer?.full_name, booking?.booking_id),
        });
        setUseSaved(!!b.signatory_signature);
        setSignature(b.signatory_signature || null);
      });
  }, [open, booking]);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const submit = async () => {
    if (!form.signatory_name.trim()) return toast.error('Signatory name is required');
    if (!signature) return toast.error('Please provide the company signature');
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/booking-forms/bookings/${booking.booking_id}/company-sign`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, signature_data: signature }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to sign');
      toast.success('Booking signed and confirmed');
      onOpenChange(false);
      onSigned?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-sign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><PenLine className="w-5 h-5 text-[#D4AF37]" /> Sign &amp; Confirm Booking</DialogTitle>
          <DialogDescription>Apply the company signature. The booking will be marked <b>Confirmed</b> and a both-party signed PDF becomes available.</DialogDescription>
        </DialogHeader>
        {!branding ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Signatory Name *</Label><Input value={form.signatory_name} onChange={set('signatory_name')} data-testid="sign-signatory-name" /></div>
              <div><Label>Title</Label><Input value={form.signatory_title} onChange={set('signatory_title')} placeholder="e.g. Director of Sales" data-testid="sign-signatory-title" /></div>
            </div>
            <div>
              <Label>Confirmation Note</Label>
              <Textarea rows={5} value={form.confirmation_note} onChange={set('confirmation_note')} data-testid="sign-confirmation-note" />
              <p className="text-xs text-slate-500 mt-1">Printed on the PDF and used as the body of the confirmation email.</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Company Signature *</Label>
                {branding.signatory_signature && (
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant={useSaved ? 'default' : 'outline'} onClick={() => { setUseSaved(true); setSignature(branding.signatory_signature); }} data-testid="sign-use-saved-btn">Use saved signature</Button>
                    <Button type="button" size="sm" variant={!useSaved ? 'default' : 'outline'} onClick={() => { setUseSaved(false); setSignature(null); }} data-testid="sign-draw-new-btn">Draw new</Button>
                  </div>
                )}
              </div>
              {useSaved && signature ? (
                <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-4 flex items-center gap-4" data-testid="sign-saved-preview">
                  <img src={signature} alt="Saved signature" className="h-16 object-contain bg-white rounded border px-2" />
                  <p className="text-sm text-emerald-800 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Saved authorised signature will be applied</p>
                </div>
              ) : (
                <SignaturePad onChange={setSignature} />
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !branding} className="bg-[#D4AF37] text-[#0F172A] hover:bg-[#B5952F] hover:text-white font-bold" data-testid="sign-confirm-btn">
            {saving ? 'Signing...' : 'Sign & Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
